// BusinessTestsInterviewsScreen.js — Assessment & Technical Tests Management
// Real API · Create tests · Send to candidates · Track results · Score breakdown

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  View, Text, StyleSheet, TouchableOpacity, ScrollView, FlatList, Modal,
  TextInput, ActivityIndicator, Alert, Dimensions, RefreshControl,
  Platform, Image, Switch, KeyboardAvoidingView,
} from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { MaterialCommunityIcons, Ionicons } from '@expo/vector-icons';
import AsyncStorage from '@react-native-async-storage/async-storage';

const { width } = Dimensions.get('window');
const API_BASE = 'https://api.jobeng.ai';
const API_KEY  = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';

const api = async (path, method='GET', body=null) => {
  const token = await AsyncStorage.getItem('auth_token')||await AsyncStorage.getItem('token');
  const res = await fetch(`${API_BASE}${path}`, { method,
    headers:{'Content-Type':'application/json','X-API-Key':API_KEY,...(token?{Authorization:`Bearer ${token}`}:{})},
    ...(body?{body:JSON.stringify(body)}:{}) });
  if (!res.ok) { const e = await res.json().catch(()=>({})); throw {status:res.status,...e}; }
  return res.json();
};

const fmtDate=d=>d?new Date(d).toLocaleDateString('en-GB',{day:'numeric',month:'short',year:'numeric'}):'—';
const fmtDT=d=>d?new Date(d).toLocaleString('en-GB',{day:'numeric',month:'short',hour:'2-digit',minute:'2-digit'}):'—';

const Avatar=({name,uri,size=44})=>{
  const [err,setErr]=React.useState(false);
  const src=(!err&&uri)?uri:`https://ui-avatars.com/api/?name=${encodeURIComponent(name||'?')}&size=128&background=random&color=fff&bold=true`;
  return <View style={{width:size,height:size,borderRadius:size/2,overflow:'hidden'}}><Image source={{uri:src}} style={{width:size,height:size}} resizeMode="cover" onError={()=>setErr(true)}/></View>;
};

const TEST_TYPES = [
  {id:'coding',    label:'Coding Test',     icon:'code-braces',    color:'#3B82F6'},
  {id:'technical', label:'Technical MCQ',   icon:'brain',          color:'#8B5CF6'},
  {id:'aptitude',  label:'Aptitude Test',   icon:'lightbulb',      color:'#F59E0B'},
  {id:'personality',label:'Personality',   icon:'account-heart',  color:'#EC4899'},
  {id:'case_study',label:'Case Study',      icon:'briefcase',      color:'#10B981'},
  {id:'portfolio', label:'Portfolio Review',icon:'folder-star',    color:'#0EA5E9'},
];

const STATUS_CFG = {
  pending:   {color:'#F59E0B',bg:'#FFFBEB',label:'Pending'},
  sent:      {color:'#3B82F6',bg:'#EFF6FF',label:'Sent'},
  completed: {color:'#10B981',bg:'#ECFDF5',label:'Completed'},
  passed:    {color:'#059669',bg:'#ECFDF5',label:'Passed'},
  failed:    {color:'#EF4444',bg:'#FEF2F2',label:'Failed'},
  expired:   {color:'#94A3B8',bg:'#F8FAFC',label:'Expired'},
};

export default function BusinessTestsInterviewsScreen({ navigation, route }) {
  const { jobId, jobTitle='Position', candidateId } = route?.params||{};
  const [companyLogo, setCompanyLogo]   = useState(null);
  const [companyName, setCompanyName]   = useState('');
  const [assessments, setAssessments]   = useState([]);
  const [candidates,  setCandidates]    = useState([]);
  const [loading,  setLoading]          = useState(true);
  const [refreshing, setRefreshing]     = useState(false);
  const [saving,   setSaving]           = useState(false);
  const [tab,      setTab]              = useState('assessments'); // assessments | results | create
  const [createModal, setCreateModal]   = useState(false);
  const [sendModal,   setSendModal]     = useState(null); // assessment obj
  const [resultModal, setResultModal]   = useState(null); // assessment obj
  const [selectedCandidates, setSelectedCandidates] = useState(new Set());

  // Create form
  const [testType,   setTestType]       = useState('coding');
  const [testTitle,  setTestTitle]      = useState('');
  const [testDuration, setTestDuration] = useState('60');
  const [testDesc,   setTestDesc]       = useState('');
  const [deadlineDays,setDeadlineDays]  = useState('3');
  const [passingScore,setPassingScore]  = useState('70');

  useEffect(()=>{loadCompanyProfile();loadAll();},[]);

  const loadCompanyProfile = async () => {
    try {
      const cached=await AsyncStorage.getItem('company_profile');
      if(cached){const cp=JSON.parse(cached);if(cp.company_name||cp.name)setCompanyName(cp.company_name||cp.name);const l=cp.logo||cp.logo_url||cp.company_logo||cp.banner_image_url||null;if(l)setCompanyLogo(l);}
      const cid=await AsyncStorage.getItem('company_id');if(!cid)return;
      const token=await AsyncStorage.getItem('auth_token')||await AsyncStorage.getItem('token');
      const res=await fetch(`${API_BASE}/api/data/company_profiles?company_id=${cid}`,{headers:{'X-API-Key':API_KEY,...(token?{Authorization:`Bearer ${token}`}:{})}});
      if(!res.ok)return;const r=await res.json();const cp=Array.isArray(r)?r[0]:(r.data?.[0]??r.data??r);
      if(cp&&typeof cp==='object'){if(cp.company_name||cp.name)setCompanyName(cp.company_name||cp.name);const l=cp.logo||cp.logo_url||cp.company_logo||cp.banner_image_url||null;if(l)setCompanyLogo(l);await AsyncStorage.setItem('company_profile',JSON.stringify(cp));}
    }catch{}
  };

  const loadAll = async (silent=false) => {
    if(!silent)setLoading(true);
    try {
      const cid = await AsyncStorage.getItem('company_id');
      await Promise.allSettled([
        (async()=>{
          try {
            const p = jobId ? `/api/data/assessments?job_id=${jobId}` : cid ? `/api/data/assessments?company_id=${cid}` : null;
            if(!p)return;
            const r=await api(p); const data=Array.isArray(r)?r:(r.data??[]);
            setAssessments(data);
          }catch{}
        })(),
        (async()=>{
          try {
            const p = jobId?`/api/data/applications?job_id=${jobId}&stage=assessment`:cid?`/api/data/applications?company_id=${cid}&stage=assessment`:null;
            if(!p)return;
            const r=await api(p);const data=Array.isArray(r)?r:(r.data??[]);
            setCandidates(data.map(a=>({
              ...a,
              id:a.id||a.applicant_id,
              name:a.candidate_name||a.name||'Unknown',
              title:a.current_title||a.position||'',
              avatar:a.avatar||a.profile_image||null,
              aiScore:a.ai_score||a.aiScore||0,
              testStatus:a.test_status||a.assessment_status||'pending',
              testScore:a.test_score||a.assessment_score||null,
            })));
          }catch{}
        })(),
      ]);
    }catch{}
    finally{setLoading(false);setRefreshing(false);}
  };

  const onRefresh=useCallback(()=>{setRefreshing(true);loadAll(true);},[jobId]);

  const createTest = async () => {
    if(!testTitle.trim()){Alert.alert('Required','Enter a test title.');return;}
    setSaving(true);
    try {
      const cid=await AsyncStorage.getItem('company_id');
      const payload={
        title:testTitle.trim(), type:testType, description:testDesc.trim(),
        duration_minutes:parseInt(testDuration)||60, passing_score:parseInt(passingScore)||70,
        deadline_days:parseInt(deadlineDays)||3, job_id:jobId, company_id:cid,
        created_at:new Date().toISOString(), status:'active',
      };
      const r=await api('/api/data/assessments','POST',payload).catch(async()=>{
        // Fallback: store locally
        const local={...payload,id:`test_${Date.now()}`};
        setAssessments(prev=>[local,...prev]);
        return local;
      });
      if(r.id||r.data?.id){setAssessments(prev=>[r.data??r,...prev]);}
      setCreateModal(false); setTestTitle(''); setTestDesc(''); setTab('assessments');
      Alert.alert('✅ Created',`"${testTitle.trim()}" assessment created`);
    }catch{Alert.alert('Error','Could not create assessment.');}
    finally{setSaving(false);}
  };

  const sendToSelected = async (assessment) => {
    if(!selectedCandidates.size){Alert.alert('Select Candidates','Please select at least one candidate.');return;}
    setSaving(true);
    try {
      const ids=[...selectedCandidates];
      await Promise.all(ids.map(id=>
        api(`/api/recruitment/applicants/${id}/technical-test`,'POST',{assessment_id:assessment?.id,job_id:jobId,job_title:jobTitle})
        .catch(()=>api(`/api/data/applications/${id}`,'PUT',{stage:'assessment',technical_test_sent:true,technical_test_date:new Date().toISOString()}))
      ));
      setCandidates(prev=>prev.map(c=>selectedCandidates.has(c.id)?{...c,testStatus:'sent'}:c));
      setSelectedCandidates(new Set()); setSendModal(null);
      Alert.alert('✅ Sent',`Test sent to ${ids.length} candidate(s)`);
    }catch{Alert.alert('Error','Some sends failed.');}
    finally{setSaving(false);}
  };

  const advanceToInterview = async (id) => {
    setSaving(true);
    try {
      await api('/api/recruitment/workflow/move','POST',{applicant_id:id,stage:'video_interview'})
            .catch(()=>api(`/api/data/applications/${id}`,'PUT',{stage:'video_interview',status:'video_interview'}));
      setCandidates(prev=>prev.filter(c=>c.id!==id));
      Alert.alert('✅ Advanced','Moved to Video Interview');
    }catch{Alert.alert('Error','Could not advance.');}
    finally{setSaving(false);}
  };

  const stats = useMemo(()=>({
    total:assessments.length, active:assessments.filter(a=>a.status==='active').length,
    candidates:candidates.length,
    sent:candidates.filter(c=>c.testStatus==='sent'||c.testStatus==='completed').length,
    completed:candidates.filter(c=>c.testStatus==='completed').length,
    passed:candidates.filter(c=>c.testStatus==='passed').length,
  }),[assessments,candidates]);

  if(loading)return(
    <View style={{flex:1,backgroundColor:'#F8FAFC'}}>
      <LinearGradient colors={['#EF4444','#DC2626']} style={{paddingBottom:20}}>
        <View style={{flexDirection:'row',alignItems:'center',paddingHorizontal:14,paddingTop:Platform.OS==='android'?36:54}}>
          <TouchableOpacity style={S.hBtn} onPress={()=>navigation?.goBack()}><Ionicons name="arrow-back" size={22} color="#fff"/></TouchableOpacity>
          <Text style={{flex:1,textAlign:'center',fontSize:17,fontWeight:'900',color:'#fff'}}>Assessments</Text>
          <View style={{width:36}}/>
        </View>
      </LinearGradient>
      <View style={{flex:1,alignItems:'center',justifyContent:'center'}}><ActivityIndicator size={40} color="#EF4444"/></View>
    </View>
  );

  return(
    <View style={{flex:1,backgroundColor:'#F8FAFC'}}>
      {/* Header */}
      <LinearGradient colors={['#EF4444','#DC2626']} style={{paddingBottom:12}}>
        <View style={{flexDirection:'row',alignItems:'center',paddingHorizontal:14,paddingTop:Platform.OS==='android'?36:54,gap:8}}>
          <TouchableOpacity style={S.hBtn} onPress={()=>navigation?.goBack()}><Ionicons name="arrow-back" size={22} color="#fff"/></TouchableOpacity>
          <TouchableOpacity onPress={()=>navigation?.navigate('CompanyProfileScreen')} style={{marginRight:4}}>
            {companyLogo?<Image source={{uri:companyLogo}} style={S.hLogo} resizeMode="cover"/>
              :<View style={S.hLogoFb}><Text style={{color:'#fff',fontSize:11,fontWeight:'900'}}>{(companyName||'CO').split(' ').slice(0,2).map(w=>w[0]?.toUpperCase()||'').join('')||'CO'}</Text></View>}
          </TouchableOpacity>
          <View style={{flex:1}}>
            <Text style={{fontSize:17,fontWeight:'900',color:'#fff'}}>Assessments</Text>
            <Text style={{fontSize:11,color:'rgba(255,255,255,0.8)',marginTop:1}}>{jobTitle} · {stats.candidates} at this stage</Text>
          </View>
          <TouchableOpacity style={[S.hBtn,{backgroundColor:'rgba(255,255,255,0.3)'}]} onPress={()=>setCreateModal(true)}>
            <MaterialCommunityIcons name="plus" size={20} color="#fff"/>
          </TouchableOpacity>
        </View>
        {/* Stats */}
        <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{paddingHorizontal:14,gap:16,paddingVertical:10}}>
          {[{label:'Tests',val:stats.total},{label:'In Stage',val:stats.candidates},{label:'Test Sent',val:stats.sent},{label:'Completed',val:stats.completed},{label:'Passed',val:stats.passed}].map(s=>(
            <View key={s.label}><Text style={{fontSize:18,fontWeight:'900',color:'#fff'}}>{s.val}</Text><Text style={{fontSize:10,color:'rgba(255,255,255,0.75)'}}>{s.label}</Text></View>
          ))}
        </ScrollView>
      </LinearGradient>

      {/* Tab bar */}
      <View style={{flexDirection:'row',backgroundColor:'#fff',borderBottomWidth:1,borderBottomColor:'#E5E7EB'}}>
        {[['assessments','Tests','clipboard-list'],['results','Candidates','account-multiple'],['create','Create','plus-circle']].map(([id,label,icon])=>(
          <TouchableOpacity key={id} style={{flex:1,flexDirection:'row',alignItems:'center',justifyContent:'center',gap:5,paddingVertical:12,borderBottomWidth:2,borderBottomColor:tab===id?'#EF4444':'transparent'}} onPress={()=>tab===id&&id==='create'?setCreateModal(true):setTab(id)}>
            <MaterialCommunityIcons name={icon} size={16} color={tab===id?'#EF4444':'#9CA3AF'}/>
            <Text style={{fontSize:13,fontWeight:tab===id?'800':'600',color:tab===id?'#EF4444':'#9CA3AF'}}>{label}</Text>
          </TouchableOpacity>
        ))}
      </View>

      {tab==='assessments'&&(
        <ScrollView contentContainerStyle={{padding:14,paddingBottom:80}} refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} colors={['#EF4444']}/>}>
          {/* Test types grid */}
          <View style={{backgroundColor:'#fff',borderRadius:16,padding:14,marginBottom:14,shadowColor:'#000',shadowOffset:{width:0,height:1},shadowOpacity:0.05,shadowRadius:4,elevation:2}}>
            <Text style={{fontSize:13,fontWeight:'800',color:'#111827',marginBottom:12}}>Assessment Types</Text>
            <View style={{flexDirection:'row',flexWrap:'wrap',gap:10}}>
              {TEST_TYPES.map(t=>(
                <TouchableOpacity key={t.id} style={{width:(width-64)/3,backgroundColor:t.color+'15',borderRadius:12,padding:12,alignItems:'center',borderWidth:1,borderColor:t.color+'30'}} onPress={()=>{setTestType(t.id);setCreateModal(true);}}>
                  <MaterialCommunityIcons name={t.icon} size={24} color={t.color}/>
                  <Text style={{fontSize:10,color:t.color,fontWeight:'700',marginTop:6,textAlign:'center'}}>{t.label}</Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>

          {assessments.length===0?(
            <View style={{alignItems:'center',paddingVertical:48}}>
              <MaterialCommunityIcons name="clipboard-text-outline" size={56} color="#E5E7EB"/>
              <Text style={{color:'#9CA3AF',marginTop:12,fontSize:15}}>No assessments yet</Text>
              <TouchableOpacity style={{backgroundColor:'#EF4444',borderRadius:12,paddingHorizontal:20,paddingVertical:11,marginTop:16,flexDirection:'row',gap:6,alignItems:'center'}} onPress={()=>setCreateModal(true)}>
                <MaterialCommunityIcons name="plus" size={16} color="#fff"/>
                <Text style={{color:'#fff',fontWeight:'700'}}>Create Assessment</Text>
              </TouchableOpacity>
            </View>
          ):assessments.map((a,i)=>{
            const typeCfg=TEST_TYPES.find(t=>t.id===a.type)||TEST_TYPES[0];
            const sCfg=STATUS_CFG[a.status]||STATUS_CFG.pending;
            return(
              <View key={a.id||i} style={S.card}>
                <View style={{flexDirection:'row',alignItems:'flex-start'}}>
                  <View style={{width:44,height:44,borderRadius:12,backgroundColor:typeCfg.color+'20',alignItems:'center',justifyContent:'center'}}>
                    <MaterialCommunityIcons name={typeCfg.icon} size={22} color={typeCfg.color}/>
                  </View>
                  <View style={{flex:1,marginLeft:12}}>
                    <Text style={{fontSize:15,fontWeight:'800',color:'#111827'}}>{a.title||'Untitled Test'}</Text>
                    <Text style={{fontSize:12,color:'#6B7280',marginTop:2}}>{typeCfg.label} · {a.duration_minutes||60} min · Pass: {a.passing_score||70}%</Text>
                    <View style={{flexDirection:'row',gap:6,marginTop:6}}>
                      <View style={{backgroundColor:sCfg.bg,borderRadius:10,paddingHorizontal:8,paddingVertical:3}}>
                        <Text style={{fontSize:10,fontWeight:'700',color:sCfg.color}}>{sCfg.label}</Text>
                      </View>
                      {a.created_at&&<Text style={{fontSize:11,color:'#9CA3AF',alignSelf:'center'}}>Created {fmtDate(a.created_at)}</Text>}
                    </View>
                    {a.description&&<Text style={{fontSize:12,color:'#6B7280',marginTop:6,lineHeight:17}} numberOfLines={2}>{a.description}</Text>}
                  </View>
                </View>
                <View style={{flexDirection:'row',gap:8,marginTop:12,paddingTop:10,borderTopWidth:1,borderTopColor:'#F1F5F9'}}>
                  <TouchableOpacity style={[S.actBtn,{backgroundColor:'#EFF6FF',flex:1}]} onPress={()=>setSendModal(a)}>
                    <MaterialCommunityIcons name="send" size={14} color="#3B82F6"/>
                    <Text style={{color:'#3B82F6',fontWeight:'700',fontSize:12}}>Send to Candidates</Text>
                  </TouchableOpacity>
                  <TouchableOpacity style={[S.actBtn,{backgroundColor:'#F0FDF4',flex:1}]} onPress={()=>setResultModal(a)}>
                    <MaterialCommunityIcons name="chart-bar" size={14} color="#10B981"/>
                    <Text style={{color:'#10B981',fontWeight:'700',fontSize:12}}>View Results</Text>
                  </TouchableOpacity>
                </View>
              </View>
            );
          })}
        </ScrollView>
      )}

      {tab==='results'&&(
        <FlatList
          data={candidates} keyExtractor={c=>String(c.id)}
          contentContainerStyle={{padding:14,paddingBottom:80}}
          refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} colors={['#EF4444']}/>}
          ListEmptyComponent={<View style={{alignItems:'center',paddingVertical:60}}><MaterialCommunityIcons name="account-group-outline" size={56} color="#E5E7EB"/><Text style={{color:'#9CA3AF',marginTop:12}}>No candidates at assessment stage</Text></View>}
          renderItem={({item:c})=>{
            const sCfg=STATUS_CFG[c.testStatus]||STATUS_CFG.pending;
            const score=c.testScore;
            const isSel=selectedCandidates.has(c.id);
            return(
              <TouchableOpacity style={[S.card,isSel&&{borderWidth:2,borderColor:'#EF4444'}]} onPress={()=>setSelectedCandidates(p=>{const n=new Set(p);n.has(c.id)?n.delete(c.id):n.add(c.id);return n;})}>
                <View style={{flexDirection:'row',alignItems:'center'}}>
                  <Ionicons name={isSel?'checkbox':'square-outline'} size={18} color={isSel?'#EF4444':'#D1D5DB'} style={{marginRight:10}}/>
                  <Avatar name={c.name} uri={c.avatar} size={44}/>
                  <View style={{flex:1,marginLeft:10}}>
                    <View style={{flexDirection:'row',alignItems:'center',gap:6}}>
                      <Text style={{fontSize:14,fontWeight:'800',color:'#111827',flex:1}} numberOfLines={1}>{c.name}</Text>
                      {score!==null&&score!==undefined&&(
                        <View style={{width:36,height:36,borderRadius:18,borderWidth:2,borderColor:score>=70?'#10B981':'#EF4444',alignItems:'center',justifyContent:'center'}}>
                          <Text style={{fontSize:11,fontWeight:'900',color:score>=70?'#10B981':'#EF4444'}}>{score}</Text>
                        </View>
                      )}
                    </View>
                    <Text style={{fontSize:12,color:'#6B7280',marginTop:2}} numberOfLines={1}>{c.title||'—'}</Text>
                    <View style={{flexDirection:'row',gap:6,marginTop:5}}>
                      <View style={{backgroundColor:sCfg.bg,borderRadius:10,paddingHorizontal:8,paddingVertical:3}}>
                        <Text style={{fontSize:10,fontWeight:'700',color:sCfg.color}}>{sCfg.label}</Text>
                      </View>
                      {c.aiScore>0&&<View style={{backgroundColor:'#F5F3FF',borderRadius:10,paddingHorizontal:8,paddingVertical:3}}><Text style={{fontSize:10,fontWeight:'700',color:'#8B5CF6'}}>AI {c.aiScore}</Text></View>}
                    </View>
                  </View>
                </View>
                {c.testStatus==='completed'||c.testStatus==='passed'?(
                  <View style={{flexDirection:'row',gap:8,marginTop:10}}>
                    <TouchableOpacity style={[S.actBtn,{backgroundColor:'#DCFCE7',flex:1}]} onPress={()=>advanceToInterview(c.id)}>
                      <MaterialCommunityIcons name="video" size={14} color="#059669"/>
                      <Text style={{color:'#059669',fontWeight:'700',fontSize:12}}>Video Interview</Text>
                    </TouchableOpacity>
                    <TouchableOpacity style={[S.actBtn,{backgroundColor:'#EF4444',flex:1}]} onPress={()=>{Alert.alert(`Reject ${c.name}?`,'',[{text:'Cancel',style:'cancel'},{text:'Reject',style:'destructive',onPress:async()=>{try{await api(`/api/data/applications/${c.id}`,'PUT',{stage:'rejected'});setCandidates(prev=>prev.filter(x=>x.id!==c.id));}catch{}}}]);}}>
                      <MaterialCommunityIcons name="close" size={14} color="#fff"/>
                      <Text style={{color:'#fff',fontWeight:'700',fontSize:12}}>Reject</Text>
                    </TouchableOpacity>
                  </View>
                ):(
                  <View style={{flexDirection:'row',gap:8,marginTop:10}}>
                    <TouchableOpacity style={[S.actBtn,{backgroundColor:'#EFF6FF',flex:1}]} onPress={()=>{setSendModal(assessments[0]||{title:'Assessment'});setSelectedCandidates(new Set([c.id]));}}>
                      <MaterialCommunityIcons name="send" size={14} color="#3B82F6"/>
                      <Text style={{color:'#3B82F6',fontWeight:'700',fontSize:12}}>Send Test</Text>
                    </TouchableOpacity>
                  </View>
                )}
              </TouchableOpacity>
            );
          }}
        />
      )}

      {/* Create Assessment Modal */}
      <Modal visible={createModal} animationType="slide" presentationStyle="pageSheet">
        <KeyboardAvoidingView behavior={Platform.OS==='ios'?'padding':'height'} style={{flex:1,backgroundColor:'#F9FAFB'}}>
          <View style={{flexDirection:'row',alignItems:'center',justifyContent:'space-between',padding:16,borderBottomWidth:1,borderBottomColor:'#E5E7EB',backgroundColor:'#fff'}}>
            <TouchableOpacity onPress={()=>setCreateModal(false)}><Ionicons name="close" size={24} color="#6B7280"/></TouchableOpacity>
            <Text style={{fontSize:16,fontWeight:'800',color:'#111827'}}>Create Assessment</Text>
            <TouchableOpacity onPress={createTest} disabled={saving}>
              {saving?<ActivityIndicator size={16} color="#EF4444"/>:<Text style={{color:'#EF4444',fontWeight:'800'}}>Create</Text>}
            </TouchableOpacity>
          </View>
          <ScrollView contentContainerStyle={{padding:16}}>
            <Text style={S.fieldLabel}>Assessment Type</Text>
            <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{gap:8,marginBottom:16}}>
              {TEST_TYPES.map(t=>(
                <TouchableOpacity key={t.id} onPress={()=>setTestType(t.id)}
                  style={{flexDirection:'row',alignItems:'center',gap:6,paddingHorizontal:12,paddingVertical:8,borderRadius:12,backgroundColor:testType===t.id?t.color:t.color+'15',borderWidth:1,borderColor:testType===t.id?t.color:t.color+'30'}}>
                  <MaterialCommunityIcons name={t.icon} size={16} color={testType===t.id?'#fff':t.color}/>
                  <Text style={{fontSize:12,color:testType===t.id?'#fff':t.color,fontWeight:'700'}}>{t.label}</Text>
                </TouchableOpacity>
              ))}
            </ScrollView>
            <Text style={S.fieldLabel}>Title *</Text>
            <TextInput style={S.input} value={testTitle} onChangeText={setTestTitle} placeholder="e.g. React Developer Technical Test" placeholderTextColor="#D1D5DB"/>
            <Text style={S.fieldLabel}>Description</Text>
            <TextInput style={[S.input,{height:80,textAlignVertical:'top'}]} value={testDesc} onChangeText={setTestDesc} placeholder="Test description and instructions…" placeholderTextColor="#D1D5DB" multiline/>
            <View style={{flexDirection:'row',gap:12}}>
              <View style={{flex:1}}>
                <Text style={S.fieldLabel}>Duration (min)</Text>
                <TextInput style={S.input} value={testDuration} onChangeText={setTestDuration} keyboardType="number-pad" placeholder="60"/>
              </View>
              <View style={{flex:1}}>
                <Text style={S.fieldLabel}>Pass Score (%)</Text>
                <TextInput style={S.input} value={passingScore} onChangeText={setPassingScore} keyboardType="number-pad" placeholder="70"/>
              </View>
              <View style={{flex:1}}>
                <Text style={S.fieldLabel}>Deadline (days)</Text>
                <TextInput style={S.input} value={deadlineDays} onChangeText={setDeadlineDays} keyboardType="number-pad" placeholder="3"/>
              </View>
            </View>
            <TouchableOpacity style={{backgroundColor:'#EF4444',borderRadius:12,padding:14,flexDirection:'row',justifyContent:'center',gap:8,alignItems:'center',marginTop:8}} onPress={createTest} disabled={saving}>
              {saving?<ActivityIndicator size={16} color="#fff"/>:<><MaterialCommunityIcons name="clipboard-plus" size={18} color="#fff"/><Text style={{color:'#fff',fontWeight:'800',fontSize:15}}>Create Assessment</Text></>}
            </TouchableOpacity>
          </ScrollView>
        </KeyboardAvoidingView>
      </Modal>

      {/* Send to candidates modal */}
      <Modal visible={!!sendModal} transparent animationType="slide">
        <View style={{flex:1,backgroundColor:'rgba(0,0,0,0.4)',justifyContent:'flex-end'}}>
          <View style={{backgroundColor:'#fff',borderTopLeftRadius:24,borderTopRightRadius:24,padding:22,maxHeight:'70%',paddingBottom:Platform.OS==='ios'?36:24}}>
            <Text style={{fontSize:16,fontWeight:'800',color:'#111827',marginBottom:4}}>Send: {sendModal?.title}</Text>
            <Text style={{fontSize:13,color:'#6B7280',marginBottom:14}}>Select candidates to send this assessment to</Text>
            <ScrollView style={{maxHeight:300}}>
              {candidates.map(c=>{const isSel=selectedCandidates.has(c.id);return(
                <TouchableOpacity key={c.id} style={{flexDirection:'row',alignItems:'center',paddingVertical:10,borderBottomWidth:1,borderBottomColor:'#F8FAFC'}} onPress={()=>setSelectedCandidates(p=>{const n=new Set(p);n.has(c.id)?n.delete(c.id):n.add(c.id);return n;})}>
                  <Ionicons name={isSel?'checkbox':'square-outline'} size={20} color={isSel?'#EF4444':'#D1D5DB'} style={{marginRight:12}}/>
                  <Avatar name={c.name} uri={c.avatar} size={36}/>
                  <View style={{flex:1,marginLeft:10}}><Text style={{fontSize:13,fontWeight:'700',color:'#111827'}}>{c.name}</Text><Text style={{fontSize:11,color:'#9CA3AF'}}>{c.title||'—'}</Text></View>
                  {c.testStatus!=='pending'&&<View style={{backgroundColor:STATUS_CFG[c.testStatus]?.bg||'#F1F5F9',borderRadius:8,paddingHorizontal:8,paddingVertical:3}}><Text style={{fontSize:10,fontWeight:'700',color:STATUS_CFG[c.testStatus]?.color||'#9CA3AF'}}>{STATUS_CFG[c.testStatus]?.label||c.testStatus}</Text></View>}
                </TouchableOpacity>
              );})}
            </ScrollView>
            <View style={{flexDirection:'row',gap:10,marginTop:14}}>
              <TouchableOpacity style={{flex:1,backgroundColor:'#F1F5F9',borderRadius:12,padding:13,alignItems:'center'}} onPress={()=>{setSendModal(null);setSelectedCandidates(new Set());}}><Text style={{color:'#6B7280',fontWeight:'700'}}>Cancel</Text></TouchableOpacity>
              <TouchableOpacity style={{flex:1,backgroundColor:'#EF4444',borderRadius:12,padding:13,alignItems:'center',flexDirection:'row',justifyContent:'center',gap:6}} onPress={()=>sendToSelected(sendModal)} disabled={saving}>
                {saving?<ActivityIndicator size={16} color="#fff"/>:<><MaterialCommunityIcons name="send" size={16} color="#fff"/><Text style={{color:'#fff',fontWeight:'700'}}>Send ({selectedCandidates.size})</Text></>}
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </View>
  );
}

const S=StyleSheet.create({
  hBtn:{width:36,height:36,borderRadius:18,backgroundColor:'rgba(255,255,255,0.2)',alignItems:'center',justifyContent:'center'},
  hLogo:{width:32,height:32,borderRadius:16,borderWidth:2,borderColor:'rgba(255,255,255,0.5)'},
  hLogoFb:{width:32,height:32,borderRadius:16,backgroundColor:'rgba(255,255,255,0.2)',borderWidth:2,borderColor:'rgba(255,255,255,0.4)',alignItems:'center',justifyContent:'center'},
  card:{backgroundColor:'#fff',borderRadius:16,padding:14,marginBottom:10,shadowColor:'#000',shadowOffset:{width:0,height:1},shadowOpacity:0.05,shadowRadius:4,elevation:2},
  actBtn:{flexDirection:'row',alignItems:'center',justifyContent:'center',gap:4,borderRadius:8,paddingVertical:8},
  fieldLabel:{fontSize:11,fontWeight:'800',color:'#9CA3AF',textTransform:'uppercase',letterSpacing:0.5,marginBottom:6},
  input:{backgroundColor:'#F9FAFB',borderWidth:1.5,borderColor:'#E5E7EB',borderRadius:12,paddingHorizontal:14,paddingVertical:11,fontSize:14,color:'#111827',marginBottom:12},
});